import Menu from '@mui/material/Menu'
import React, { memo } from 'react'
import tw from 'twin.macro'
import { PopoverOrigin, Tooltip, Typography } from '@mui/material'
import { MenuActionItem } from 'src/interfaces/ui'

interface Props {
  items: MenuActionItem[]
  isOpen?: boolean
  anchorEl?: any
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  itemClassName?: any
  onClose(): void
}

/**
 * apps/dashboard's `ActionMenu`: an 8px-radius paper with the one design-system shadow, items
 * as a 20px icon well plus a subtitle1 label, quiet grey hover. Items with `href` render as
 * links; the rest are plain menu items. A disabled item stays visible, greyed, with its reason
 * as a tooltip — so an action the API does not support yet is discoverable rather than absent.
 */
const ActionMenuComponent = ({
  items,
  isOpen,
  anchorEl,
  anchorOrigin,
  itemClassName,
  transformOrigin,
  onClose,
}: Props) => {
  return (
    <Menu
      id="menu-list"
      open={Boolean(isOpen)}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75,
            minWidth: 180,
            borderRadius: '8px',
            boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.07)',
          },
        },
        list: { sx: { py: 1 } },
      }}
    >
      {items.map((item) => {
        const {
          label,
          color,
          icon,
          rightIcon,
          className,
          downloadHref,
          href,
          disabled,
          disabledReason,
          dataTestId,
          onClick,
        } = item

        const content = (
          <div
            role="menuitem"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            data-testid={dataTestId}
            css={[
              tw`flex flex-row items-center gap-2 px-3 py-2`,
              disabled
                ? tw`cursor-default opacity-40`
                : tw`cursor-pointer hover:bg-shades-50 active:bg-shades-100`,
              itemClassName,
              className,
            ]}
            onClick={disabled ? undefined : onClick}
            onKeyDown={(event) => {
              if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                onClick()
              }
            }}
          >
            {icon ? (
              <span css={tw`flex h-5 w-5 shrink-0 items-center justify-center`}>{icon}</span>
            ) : null}
            <Typography variant="subtitle1" css={tw`text-shades-1000`} style={{ color }}>
              {label}
            </Typography>
            {rightIcon}
          </div>
        )

        const linkOrContent =
          href || downloadHref ? (
            <a
              key={label}
              target="_blank"
              rel="noreferrer"
              href={href || downloadHref}
              download={Boolean(downloadHref)}
              css={tw`text-shades-1000 no-underline`}
            >
              {content}
            </a>
          ) : (
            <React.Fragment key={label}>{content}</React.Fragment>
          )

        if (disabled && disabledReason) {
          return (
            <Tooltip key={label} title={disabledReason} placement="left">
              <span>{linkOrContent}</span>
            </Tooltip>
          )
        }

        return linkOrContent
      })}
    </Menu>
  )
}

export const ActionMenu = memo(ActionMenuComponent)
